import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum AthenaClubPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // SKU / ID of the item
  skuDescription = 'skuDescription', // descriptive name of the item (renamed from 'name')
  details = 'details', // verbose description of the item, optional (renamed from 'description')
  vendorSku = 'vendorSku', // vendor's SKU identifier, optional
  vendorSkuDescription = 'vendorSkuDescription', // vendor's SKU description, optional
  uom = 'uom', // unit of measure
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const AthenaClubPurchaseOrderAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
  combineAddressLines: yup.string().nullable().optional(),
})
export type AthenaClubPurchaseOrderAddressInfo = yup.InferType<
  typeof AthenaClubPurchaseOrderAddressInfoSchema
>

const miscChargeSchema = yup.object().shape({
  label: yup.string().nullable().optional(), // e.g. "Tax", "Shipping", "Freight"
  value: yup.string().nullable().optional(), // dollar amount
  percentage: yup.string().nullable().optional(), // e.g. "5" → renders "Tax (5%)"
})

const lineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

const AthenaClubPurchaseOrderItemHeadersSchema = yup.object().shape({
  itemNumber: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const AthenaClubPurchaseOrderFileDataSchema = yup.object().shape({
  issuer: AthenaClubPurchaseOrderAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  requestedShipDate: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: AthenaClubPurchaseOrderAddressInfoSchema,
  vendor: AthenaClubPurchaseOrderAddressInfoSchema,
  itemHeaders: AthenaClubPurchaseOrderItemHeadersSchema,
  items: yup.array().of(lineItemSchema).required(),
  items2: yup.array().of(lineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  requestedShipmentMethod: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  termsAndConditionsUrl: yup.string().nullable().optional(),
  termsAndConditionsLabel: yup.string().nullable().optional(),
  miscCharges: yup.array().of(miscChargeSchema).optional(),
  currency: yup.string().nullable().optional(),
})

export type AthenaClubPurchaseOrderFileData = yup.InferType<
  typeof AthenaClubPurchaseOrderFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<AthenaClubPurchaseOrderFileData>

export const athenaClubPurchaseOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: 'd9f57e06-4026-44c3-9212-36ba37003136',
    companyName: 'e244a829-644c-4dd7-90ec-a21b10c2a226',
    addressLine1: 'cd37f58d-796d-4b89-9f37-58b738844eab',
    addressLine2: '9f48bfe1-8ae0-4d24-9f1c-7070540424cc',
    city: '1ce31df7-dd4f-4b20-8301-77c875f8089b',
    state: '917796ab-d289-4a52-a6ea-108ec6b2e402',
    zipCode: '811c2645-2130-4b37-9597-67295c0233c9',
    country: '0768264c-b9a5-4bef-b79e-8d3d5bb00012',
    phoneNumber: 'e0aca90b-19e5-49b8-9479-28add8e1632a',
    emailAddress: '70992c74-f54b-4b09-a507-a7d9017a5727',
    displayMultipleLines: '2ff884a6-e5d3-4800-bc35-177c7680842c',
    combineAddressLines: '52c6f3ef-e61f-483f-ab8a-2379cb357149',
  },
  orderNumber: 'c067243a-6638-4dc1-94f6-0cd5e1ab5232',
  paymentTerms: '34c64a91-ba95-4a34-a839-28f3e4523ce2',
  freightTerms: '59202ad7-dd6c-4a41-a8be-7fe661de60b0',
  dateIssued: '71b65428-4766-4b6d-a444-a6b3ab66a615',
  requestedShipDate: 'c4c00453-2959-4db2-b247-d974f06e55b8',
  dateReady: 'd9a97695-7279-4e17-aa5e-39ba1be748ad',
  destination: {
    attn: 'ace8944f-e7b3-4c0c-988f-111020ccde17',
    companyName: '46ac3461-5c28-4945-9de5-3df87a4ed4ba',
    addressLine1: '9d783f9f-5387-42b0-94a2-9dffc640f4f6',
    addressLine2: 'e004bdf0-b2e3-4b5c-ac78-e0ecd95f8923',
    city: 'e6f4a947-f29b-45ba-895b-adfacf8824a2',
    state: '40e0fc5d-6bbc-4478-a6ec-f1412ccf1527',
    zipCode: '97a1dfe5-b6c4-4650-ae06-663254afda68',
    country: '119a9071-a3cb-4901-9517-3d558ea99373',
    phoneNumber: 'a3b57a23-96c0-417b-9a72-f1123206d887',
    emailAddress: '95eb9f5d-b980-46b7-9669-36878a14f09e',
    displayMultipleLines: '6d0a344b-9695-4cb0-bc04-2dcfa8e4c9a2',
    combineAddressLines: 'dc8943da-0c9d-4c76-94b3-3d5e563e2007',
  },
  vendor: {
    attn: '1d6d1d0f-97a6-4a0f-9bdf-0829f62f3a73',
    companyName: 'd40d38b7-d25b-471c-afce-221a2cb0cd5e',
    addressLine1: 'b0725dc6-e1a0-49ef-b4ad-07b271ce37ab',
    addressLine2: 'a7ec0a02-dfc4-426d-9049-f88044e188e5',
    city: '1d1648e7-9487-428d-b88b-1cf811280721',
    state: '7b96fe84-42d2-41d9-af86-5013e0372608',
    zipCode: '29031d74-0e51-45ba-a32d-231703bd8a21',
    country: '594d6258-e30e-4b27-8422-1e965dd73ae8',
    phoneNumber: '4d43a359-21ab-4d21-9bf8-554859832af2',
    emailAddress: '59384a6d-cfad-403a-b734-e7ff9b635282',
    displayMultipleLines: 'fb4128f2-43d1-4d05-8979-04f70e292932',
    combineAddressLines: '3ca4f7cb-6e54-43fd-94fb-da6222207d81',
  },
  itemHeaders: {
    itemNumber: '7fa89abd-6547-482f-a57f-01eb8bf709f0',
    itemName: '8866dca1-882e-4988-ac14-ae57890d4671',
    skuDescription: 'eebd0cc8-0d9b-4061-b234-5a99c2497c55',
    details: '7295d34d-9e98-424f-82ff-414abd8986f1',
    vendorSku: '5cce6e77-47d5-4b51-ae03-71e523857ffd',
    vendorSkuDescription: 'c70c9543-f8cf-4aae-bfa3-a061c5bc88dd',
    uom: '14d50093-c4a9-475c-81c3-6f71fb26452c',
    itemQty: '9e8cc1b1-9102-431d-a7e6-15ad633a5e4e',
    unitPrice: 'b9ebeef7-56fd-4196-84a1-ab385448d242',
    lineTotal: 'c9b50f83-00a5-464e-a93c-7335cd15153b',
  },
  items: [
    {
      itemNumber: null,
      itemName: '96aaf680-48d8-4fa5-a45c-fd473ea3ed6b',
      skuDescription: 'c2263173-5573-4f85-a2a6-67d7af6ff0ce',
      details: '292bea4c-b043-4639-a935-f4094b725622',
      vendorSku: 'a36736b2-f453-4fa1-acf5-6ba8b8a5d91a',
      vendorSkuDescription: '2057b8e9-adc2-4819-8fd1-1e1c98e27588',
      uom: 'ac79fae4-3352-4897-a19c-f4c9006736af',
      itemQty: '560a416b-0e00-48bc-8174-41b9ec82feb6',
      unitPrice: 'ae9c4ed3-41dc-49ba-af8d-20db77efa958',
      lineTotal: 'e873cf5f-60e4-4f40-a803-f51088279de4',
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'baf7f412-9201-49f5-8cba-fa4d7aa9e1a0',
      skuDescription: 'bb0dcea1-99a3-4d24-8536-58fa70b9a1de',
      details: '077461bc-257d-4c3f-961d-3d477ba86e66',
      vendorSku: '67acca09-d052-402d-97c0-d441f71c091e',
      vendorSkuDescription: '52f20610-c8e0-4478-bbd7-2cd5810a7937',
      uom: '7a91cae3-8e31-4584-a5fb-e5127fa05e38',
      itemQty: '5be0e187-95d1-4b56-8764-017d9103904f',
      unitPrice: '80cb439a-c5f1-46a6-81e5-abe8b8c4e570',
      lineTotal: '1a09b297-23ae-4ac8-81cd-01829bfffe10',
    },
  ],
  dontRoundUnitPrice: 'c7d4b3c7-d1b9-4078-aec3-c173152838c9',
  agreement: 'fb6c5c48-0552-4091-ba78-208c2cbcae31',
  requestedShipmentMethod: '2f00c690-3a5a-490d-ba93-ff7bb1eb4737',
  instructions: '3f975f05-da9e-4aa6-a663-36bf6a594b30',
  useLocaleQuantityFormatting: '425bc224-9174-4b5f-a332-c0f2715a52a4',
  termsAndConditionsUrl: '444024ae-1b45-4a0a-9cdd-0e25a978eafc',
  termsAndConditionsLabel: 'cc559a86-e541-43d0-b03a-9bf481f12e86',
  miscCharges: [
    {
      label: '742e277d-9c39-4bf5-9152-9c1472c00d16',
      value: 'a843fd6f-4ea2-4f9d-8ba2-7614f36984d5',
      percentage: '43b557f4-e018-4912-913b-c7b4a7258e8d',
    },
  ],
  currency: 'bbfa021d-c1a2-4f17-bfc1-e50623bc2b0d',
}
